import { Link } from "@/lib/router-compat";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Clock, Calendar, Tag } from "lucide-react";
import { authorSlug, authorDisplayName, authorRole, parseAuthors } from "@/lib/authorSlug";
import { supabase } from "@/integrations/supabase/client";
import { getDateLocale, uiT } from "@/i18n/ui";
import { FILTER_DISPLAY_NAMES } from "@/components/blog/BlogFilterSidebar";


interface BlogPostHeroProps {
  title: string;
  category?: string | null;
  publishedAt?: string | null;
  updatedAt?: string | null;
  author?: string | null;
  featuredImage?: string | null;
  readingTime: number;
  summary?: string | null;
  tags?: string[] | null;
  hideBreadcrumb?: boolean;
  postId?: string;
  postLang?: string;
  translationOf?: string | null;
}

const BlogPostHero = ({
  title,
  category,
  publishedAt,
  updatedAt,
  author,
  featuredImage,
  readingTime,
  summary,
  tags,
  hideBreadcrumb = false,
  postId,
  postLang,
  translationOf,
}: BlogPostHeroProps) => {
  const lang = useLanguage();
  const t = uiT(lang);
  const dateLoc = getDateLocale(lang);
  const pillLabels = Array.from(
    new Set([category, ...(tags ?? [])].filter(Boolean) as string[])
  )
    .filter((label) => !label.startsWith("_") && label.toLowerCase() !== "latam-only")
    .slice(0, 3);

  const authors = author ? parseAuthors(author) : [];
  const authorSlugs = authors.map(a => authorSlug(a));

  const { data: authorProfiles } = useQuery({
    queryKey: ["author-avatars", authorSlugs],
    queryFn: async () => {
      const { data } = await supabase
        .from("authors")
        .select("avatar_url, name, slug")
        .in("slug", authorSlugs);
      return data ?? [];
    },
    enabled: authorSlugs.length > 0,
  });

  const profileMap = new Map(
    (authorProfiles ?? []).map(p => [p.slug, p])
  );

  return (
    <section className="relative min-h-[440px] lg:min-h-[500px] flex items-end overflow-hidden">
      {/* Background image */}
      {featuredImage ? (
        <img
          src={featuredImage}
          alt={title || ""}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-primary" />
      )}
      {/* Dark gradient overlay — richer */}
      <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/85 to-primary/55" />

      <div className="relative z-10 container mx-auto px-6 lg:px-10 max-w-6xl pb-14 pt-52">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Breadcrumb */}
          {!hideBreadcrumb && (
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-primary-foreground/50">
                <li>
                  <Link to={postLang === "es" ? "/es" : lang === "zh-cn" ? "/zh-cn" : "/"} className="hover:text-primary-foreground transition-colors">
                    {t("home")}
                  </Link>
                </li>
                <li aria-hidden="true">&gt;</li>
                <li>
                  <Link to={postLang === "es" ? "/es/blog" : lang === "zh-cn" ? "/zh-cn/blog" : "/en/blog"} className="hover:text-primary-foreground transition-colors">
                    {t("blog")}
                  </Link>
                </li>
                <li aria-hidden="true">&gt;</li>
                <li className="text-primary-foreground/70 truncate max-w-[200px]">
                  {title}
                </li>
              </ol>
            </nav>
          )}


          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-primary-foreground tracking-tight leading-[1.2] sm:leading-[1.22]">
            {title}
          </h1>

          {/* Summary */}
          {summary && (() => {
            const cleaned = summary
              .replace(/\[?!\[[^\]]*\]\([^)]*\)\]?(\([^)]*\))?/g, "")
              .replace(/\[?Copy link\]?\([^)]*\)/g, "")
              .replace(/\[?(X \(Twitter\)|LinkedIn|WhatsApp|Copy link|Mail)\]?\([^)]*\)/g, "")
              .replace(/Mail\]\(mailto:[^)]*("[^"]*")?\)/g, "")
              .replace(/\\\\/g, "")
              .replace(/\\$/gm, "")
              .replace(/\s{2,}/g, " ")
              .trim();
            return cleaned.length > 10 ? (
              <p className="mt-5 text-base sm:text-lg text-primary-foreground/65 leading-relaxed max-w-3xl">
                {cleaned}
              </p>
            ) : null;
          })()}

          {/* Author & meta row */}
          <div className="mt-7 flex flex-wrap items-center gap-4 text-sm text-primary-foreground/55">
            {/* Multiple authors */}
            {authors.length > 0 && (
              <div className="flex items-center gap-3">
                {/* Stacked avatars */}
                <div className="flex items-center -space-x-2">
                  {authors.map((a) => {
                    const slug = authorSlug(a);
                    const profile = profileMap.get(slug);
                    return (
                      <Link
                        key={slug}
                        to={localizeHref(`/en/author/${slug}`, lang)}
                        className="relative hover:z-10 transition-transform hover:scale-110"
                      >
                        {profile?.avatar_url ? (
                          <img
                            src={profile.avatar_url}
                            alt={authorDisplayName(a)}
                            className="w-11 h-11 rounded-full object-cover ring-2 ring-primary/80 shrink-0"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-primary-foreground/15 flex items-center justify-center text-primary-foreground/70 text-sm font-bold shrink-0 ring-2 ring-primary/80">
                            {authorDisplayName(a).charAt(0)}
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
                {/* Names */}
                <div className="text-left">
                  {authors.length === 1 ? (
                    <Link
                      to={localizeHref(`/en/author/${authorSlug(authors[0])}`, lang)}
                      className="hover:opacity-80 transition-opacity"
                    >
                      <span className="font-semibold text-primary-foreground/85 block text-[0.9375rem]">
                        {authorDisplayName(authors[0])}
                      </span>
                      {authorRole(authors[0]) && (
                        <span className="text-xs text-primary-foreground/45 block mt-0.5 max-w-[280px] truncate">
                          {authorRole(authors[0])}
                        </span>
                      )}
                    </Link>
                  ) : (
                    <div className="flex flex-wrap items-center gap-x-1">
                      {authors.map((a, i) => (
                        <span key={authorSlug(a)}>
                          <Link
                            to={localizeHref(`/en/author/${authorSlug(a)}`, lang)}
                            className="font-semibold text-primary-foreground/85 text-[0.9375rem] hover:opacity-80 transition-opacity"
                          >
                            {authorDisplayName(a)}
                          </Link>
                          {i < authors.length - 1 && (
                            <span className="text-primary-foreground/45">{i === authors.length - 2 ? " & " : ", "}</span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {authors.length > 0 && publishedAt && (
              <span className="w-1 h-1 rounded-full bg-primary-foreground/25" aria-hidden="true" />
            )}
            {publishedAt && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={14} />
                {new Date(publishedAt).toLocaleDateString(dateLoc, {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            )}
            {updatedAt && publishedAt && updatedAt.slice(0, 10) !== publishedAt.slice(0, 10) && (
              <span className="inline-flex items-center gap-1.5 text-primary-foreground/40">
                · {t("updated")}{" "}
                {new Date(updatedAt).toLocaleDateString(dateLoc, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            )}
            {(publishedAt || authors.length > 0) && (
              <span className="w-1 h-1 rounded-full bg-primary-foreground/25" aria-hidden="true" />
            )}
            <span className="inline-flex items-center gap-1.5">
              <Clock size={14} />
              {readingTime} {t("minRead")}
            </span>
          </div>

          {/* Tag pills — bottom of hero */}
          {pillLabels.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-6">
              {pillLabels.map((label) => (
                <Link
                  key={label}
                  to={`${postLang === "es" ? "/es/blog" : "/en/blog"}?filter=${encodeURIComponent(label)}`}
                  className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors"
                >
                  <Tag size={13} className="text-primary-foreground/60 shrink-0" strokeWidth={2} />
                  <span className="text-xs font-medium text-primary-foreground/75">{FILTER_DISPLAY_NAMES[label] ?? label}</span>
                </Link>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
export default BlogPostHero;